import { useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import type { CatalogEntry, DataDefField } from '@safetyhub/shared';
import { CatalogEntryPicker } from './CatalogEntryPicker';
import type { ColumnDefaults } from './datasetExcelImport';
import { DefaultValueControl, ImportPreviewStep, mappedColumnCount } from './datasetImportShared';
import {
  autoMatchCatalogColumns,
  buildRowsFromCatalog,
  collectCatalogRowFields,
  type BuildCatalogRowsResult,
  type CatalogColumnMapping,
  type CatalogRowField,
} from './datasetCatalogImport';

/**
 * 직접 입력 데이터셋(manual dataDef)에 **카탈로그 행을 저작 시점에 박제**하는 모달.
 *
 * 엑셀 가져오기(DatasetExcelImportModal map 모드)의 카탈로그 판 — 단계 골격(원천 → 칼럼 매핑 →
 * 미리보기 → 반영)과 스타일 결을 같이 쓴다. 다른 것은 원천뿐이다: 파일 파싱 대신 **지금 선택된
 * 실 데이터 컨텍스트로 서버 resolve한 카탈로그 행**을 받는다(조회 채널은 호출부가 주입 —
 * 서버 prefill 채널(resolvePrefill)과 동형이라 동적 초기 행이 주는 것과 같은 행을 본다).
 *
 * ⚠️ **동적 축(dataDef.prefillFrom)과 정반대다**: 초기 행 지정은 매 보고서 작성 시점의 최신
 * 값을 읽지만, 이 모달은 **지금 조회한 값을 기본행으로 박아 둔다** — 이후 카탈로그가 바뀌어도
 * 양식은 그대로다. 카탈로그 참조는 저장하지 않는다(가져온 행만 남는다).
 *
 * 칼럼은 불변이다(map 모드와 같은 이유 — key는 셀 바인딩·작성폼 파생이 붙잡고 있는 계약).
 * 값 변환·자동 매칭은 전부 순수 유틸(datasetCatalogImport.ts)이 맡고 여기서는 그린다.
 */

/** 반영 결과 — 호출부가 defaultRows를 추가/교체한다. */
export interface DatasetCatalogImportResult {
  rows: Record<string, unknown>[];
  replace: boolean;
}

export interface DatasetCatalogImportModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (result: DatasetCatalogImportResult) => void;
  /** 대상 데이터셋의 칼럼(불변) — 여기로 넣을 카탈로그 필드만 고른다. */
  fields: readonly DataDefField[];
  /** 기존 기본행 수 — 추가/교체 선택 문구에 쓴다. */
  existingRowCount: number;
  /** 대상 데이터셋 표시명(제목 보조 문구). */
  datasetLabel?: string;
  /**
   * 원천 후보 entry — **행 배열을 줄 수 있는 것만**(EditorPage의 datasetPrefillEntries 정본을
   * 그대로 주입해, 초기 행 지정과 고를 수 있는 범위가 같다).
   */
  entries: CatalogEntry[];
  /**
   * 단수 entry의 배열 반환 필드 후보 — EditorPage의 datasetRowsReturnPaths 정본 주입
   * (list entry는 빈 목록 = 값 자체가 행 배열이라 고를 것이 없다).
   */
  returnPaths: (entry: CatalogEntry | undefined) => { path: string; label: string }[];
  /** 실 데이터 컨텍스트 선택 여부 — 없으면 조회할 수 없어 안내만 한다. */
  hasPreviewContext: boolean;
  /**
   * 카탈로그 행 조회 — 호출부가 서버 prefill 채널(합성 dataDef.prefillFrom)로 구현한다.
   * 실패는 throw(Error.message를 그대로 보여 준다).
   */
  fetchRows: (catalogKey: string, select?: string) => Promise<Record<string, unknown>[]>;
}

type Step = 'source' | 'columns' | 'preview';

/**
 * 박제 행 수 소프트 경고 임계 — 넘어도 막지 않는다(가져온 행은 전부 템플릿 JSON에 저장되므로
 * 편집기·저장 페이로드가 무거워진다는 안내일 뿐, 유효한 사용일 수 있다).
 */
const STAMP_ROW_WARN_LIMIT = 500;

export function DatasetCatalogImportModal({
  open,
  onClose,
  onApply,
  fields,
  existingRowCount,
  datasetLabel,
  entries,
  returnPaths,
  hasPreviewContext,
  fetchRows,
}: DatasetCatalogImportModalProps) {
  // 상태 초기화는 **마운트가 곧 초기 상태**다 — 호출부가 조건부로 마운트한다(엑셀 모달과 동일:
  // 상시 마운트 + open 리셋 effect는 재열기 첫 프레임에 직전 세션이 비친다).
  const [step, setStep] = useState<Step>('source');
  const [catalogKey, setCatalogKey] = useState<string | undefined>(undefined);
  const [select, setSelect] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 조회한 카탈로그 행 원본(조회 시점 값) — null이면 아직 조회 전. */
  const [sourceRows, setSourceRows] = useState<Record<string, unknown>[] | null>(null);
  const [mapping, setMapping] = useState<CatalogColumnMapping>({});
  const [defaults, setDefaults] = useState<ColumnDefaults>({});
  const [replace, setReplace] = useState(false);

  /** 지금 들고 있는 조회 결과·자동 매칭을 만든 근거 — `entry.key:select`. 같으면 다시 조회하지 않는다. */
  const fetchedBasisRef = useRef<string | null>(null);

  const entry = entries.find((e) => e.key === catalogKey);
  const paths = returnPaths(entry);
  /** 단수 entry는 배열 반환 필드를 골라야 행이 된다 — list entry는 select 없이 전체가 행. */
  const needsSelect = !!entry && paths.length > 0;
  const canFetch = !!entry && (!needsSelect || !!select) && hasPreviewContext && !loading;

  /** 매핑 후보(원천 필드) — 선언(returns) + 관측(조회 행 키). 조회 후에만 의미가 있다. */
  const sourceFields: CatalogRowField[] = useMemo(
    () => collectCatalogRowFields(entry, select, sourceRows ?? []),
    [entry, select, sourceRows],
  );

  // 미리보기 단계에서만 전 행을 변환한다(엑셀 모달과 같은 이유 — 매핑 편집 중 재계산 방지).
  const built = useMemo<BuildCatalogRowsResult>(() => {
    if (step !== 'preview' || fields.length === 0 || !sourceRows) {
      return { rows: [], warnings: [], defaultWarnings: [] };
    }
    return buildRowsFromCatalog({ fields, sourceRows, mapping, defaults });
  }, [step, fields, sourceRows, mapping, defaults]);

  /** 원천 단계 "다음" — 지금 컨텍스트로 행을 조회하고 자동 매칭 후 칼럼 단계로. */
  const handleFetch = async () => {
    if (!entry) return;
    // 근거(원천·행 배열 필드)가 그대로면 이미 조회한 행·매핑을 쓴다 — "이전"으로 돌아갔다
    // 오는 것만으로 사용자가 고친 매핑·기본값이 자동 매칭으로 덮이면 안 된다(엑셀 모달
    // columnsBasisRef 동형).
    const basis = `${entry.key}:${needsSelect ? (select ?? '') : ''}`;
    if (fetchedBasisRef.current === basis && sourceRows !== null) {
      setStep('columns');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchRows(entry.key, needsSelect ? select : undefined);
      fetchedBasisRef.current = basis;
      setSourceRows(rows);
      // 자동 매칭은 조회 결과 기준으로 다시 만든다 — 관측 키(서버 파생 필드)까지 후보에 든다.
      setMapping(
        autoMatchCatalogColumns(fields, collectCatalogRowFields(entry, select, rows)),
      );
      setDefaults({});
      setStep('columns');
    } catch (err) {
      setError((err as Error).message || '시스템 조회 행을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const title = '시스템 조회에서 행 가져오기';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        datasetLabel ? (
          <span>
            {title} <span className="text-sm font-normal text-gray-400">— {datasetLabel}</span>
          </span>
        ) : (
          title
        )
      }
      ariaLabel={title}
      size="xl"
      // ⛔ 예외(이유② — 포탈 안 된 떠 있는 UI): 본문의 `CatalogEntryPicker`→`CatalogCombobox`가
      // 드롭다운을 `absolute z-30 … max-h-60`(role=listbox)으로 그리고 **포탈을 쓰지 않는다**.
      // base가 본문에 `overflow-y-auto`를 걸면 그 목록이 본문 경계에서 **새로 잘린다** —
      // 이 레포 규약은 드롭다운을 `createPortal`+`fixed`로 탈출시키는 것이고 그 자리는 아직
      // 그 처분을 받지 않았다(처분되면 이 예외를 걷을 수 있다).
      bodyScroll={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          {step !== 'source' && (
            <Button
              variant="secondary"
              onClick={() => setStep(step === 'preview' ? 'columns' : 'source')}
            >
              이전
            </Button>
          )}
          {step === 'source' && (
            <Button variant="primary" onClick={() => void handleFetch()} disabled={!canFetch}>
              {loading ? '조회 중…' : '조회하고 다음'}
            </Button>
          )}
          {step === 'columns' && (
            <Button
              variant="primary"
              onClick={() => setStep('preview')}
              disabled={fields.length === 0}
            >
              미리보기
            </Button>
          )}
          {step === 'preview' && (
            <Button
              variant={replace ? 'danger' : 'primary'}
              onClick={() => onApply({ rows: built.rows, replace })}
              disabled={built.rows.length === 0}
            >
              {replace
                ? `기존 행 지우고 ${built.rows.length}행 넣기`
                : `${built.rows.length}행 추가`}
            </Button>
          )}
        </>
      }
    >
      {error ? (
        <p className="mb-3 rounded bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-700">
          {error}
        </p>
      ) : null}

      {/* ① 원천(카탈로그) 선택 */}
      {step === 'source' ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-600">
            시스템 조회를 <b>지금</b> 실행해 그 행들을 이 데이터셋의 <b>기본행으로 박아 둡니다</b> —
            이후 조회 결과가 바뀌어도 양식은 그대로입니다. 매 보고서 작성 시점의 최신 값이
            필요하면 이 모달이 아니라 「시스템 조회에서 초기 행」 지정을 쓰세요.
          </p>
          {!hasPreviewContext ? (
            <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
              조회할 실 데이터 컨텍스트가 없습니다 — 미리보기 탭에서 실 데이터 컨텍스트(조직·현장
              등)를 먼저 선택하세요. 조회 결과는 그 컨텍스트의 값입니다.
            </p>
          ) : null}
          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">데이터 소스</p>
            <CatalogEntryPicker
              entries={entries}
              value={catalogKey}
              allowClear
              placeholder="데이터 소스 검색·선택…"
              onChange={(k) => {
                setCatalogKey(k);
                setSelect(undefined);
                // 원천이 바뀌면 이전 조회 결과·매핑은 다른 원천의 것이다 — 비운다.
                setSourceRows(null);
                setError(null);
              }}
            />
          </div>
          {needsSelect ? (
            <div>
              <label
                className="mb-1 block text-sm font-medium text-gray-700"
                htmlFor="dataset-catalog-import-select"
              >
                행 배열 필드 — 이 목록의 어느 배열을 행으로 가져올지 고르세요
              </label>
              <select
                id="dataset-catalog-import-select"
                value={select ?? ''}
                onChange={(e) => {
                  setSelect(e.target.value || undefined);
                  setSourceRows(null);
                }}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">— 선택하세요 —</option>
                {paths.map((p) => (
                  <option key={p.path} value={p.path}>
                    {p.label} ({p.path})
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <ul className="space-y-1 rounded bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500">
            <li>· 조회는 지금 선택된 실 데이터 컨텍스트 기준입니다(작성 시점 초기 행과 같은 값).</li>
            <li>· 어느 조회 필드를 어느 칼럼에 넣을지는 다음 단계에서 고릅니다.</li>
            <li>· 가져온 뒤에는 조회와 연결이 남지 않습니다 — 행만 저장됩니다.</li>
          </ul>
        </div>
      ) : null}

      {/* ② 칼럼 매핑 — 엑셀 map 모드와 같은 결(칼럼 불변, 미매핑은 칼럼별 기본값). */}
      {step === 'columns' ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-600">
            조회된 행 <b>{sourceRows?.length ?? 0}개</b> — 데이터셋 칼럼마다 가져올 조회
            필드를 고르세요. 이름이 같은 필드는 미리 연결해 두었습니다. 연결하지 않은 칼럼은{' '}
            <b>모든 행에 같은 값</b>을 넣습니다(비워 두면 빈 값).
          </p>
          {fields.length === 0 ? (
            <p className="text-sm text-gray-400">
              이 데이터셋에는 칼럼이 없습니다 — 먼저 칼럼을 추가하세요.
            </p>
          ) : (
            <div className="space-y-1.5">
              {fields.map((f) => {
                const source = mapping[f.key];
                const unmapped = source === null || source === undefined;
                return (
                  <div key={f.key} className="flex items-center gap-2">
                    <span
                      className="w-32 shrink-0 truncate text-xs text-gray-700"
                      title={`${f.label || f.key} (${f.key})`}
                    >
                      {f.label || f.key}
                    </span>
                    <select
                      aria-label={`${f.label || f.key} 칼럼에 넣을 조회 필드`}
                      value={source ?? ''}
                      onChange={(e) =>
                        setMapping((prev) => ({
                          ...prev,
                          [f.key]: e.target.value === '' ? null : e.target.value,
                        }))
                      }
                      className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs"
                    >
                      <option value="">— 조회에서 안 가져옴 —</option>
                      {sourceFields.map((s) => (
                        <option key={s.path} value={s.path}>
                          {s.label === s.path ? s.path : `${s.label} (${s.path})`}
                        </option>
                      ))}
                    </select>
                    {unmapped ? (
                      <DefaultValueControl
                        field={f}
                        value={defaults[f.key] ?? ''}
                        onChange={(v) => setDefaults((prev) => ({ ...prev, [f.key]: v }))}
                      />
                    ) : (
                      <span className="w-32 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-400">
            연결된 칼럼 {mappedColumnCount(fields, mapping)} / {fields.length}
          </p>
        </div>
      ) : null}

      {/* ③ 미리보기 + 반영 방식 — 엑셀 모달과 공용(datasetImportShared). */}
      {step === 'preview' ? (
        <ImportPreviewStep
          fields={fields}
          rows={built.rows}
          warnings={built.warnings}
          defaultWarnings={built.defaultWarnings}
          summary={
            <>
              가져올 행 <b>{built.rows.length}개</b>
            </>
          }
          emptyMessage="가져올 행이 없습니다. 선택한 컨텍스트에 이 조회의 데이터가 있는지 확인해주세요."
          warningRowLabel={(n) => `조회 결과 ${n}번째 행`}
          notice={
            built.rows.length > STAMP_ROW_WARN_LIMIT ? (
              <p className="rounded bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-700">
                {built.rows.length}행을 템플릿에 박아 둡니다 — 편집기가 무거워질 수 있습니다.
              </p>
            ) : undefined
          }
          applyMode={{
            radioName: 'dataset-catalog-apply-mode',
            existingRowCount,
            replace,
            onReplaceChange: setReplace,
          }}
        />
      ) : null}
    </Modal>
  );
}
